import React from "react";
import { Box, Typography } from "@mui/material";
import { ADMIN_TOP_CARD_HEIGHT } from "@/utils/adminTableStyles";

/**
 * Base admin stat card. All app-themed variants derive from this component.
 * Changing layout here propagates to every themed variant.
 */
const AdminStatCard = ({
  gradientFrom,
  gradientTo,
  borderColor,
  titleColor,
  valueColor,
  iconGhostColor = "white",
  title,
  subtitle,
  value,
  Icon,
  valueFormatter,
  caption,
  /** Set true when the value is a text string rather than a number — uses smaller font with wrapping */
  textValue = false,
}) => {
  const display = typeof valueFormatter === "function" ? valueFormatter(value) : value;

  return (
    <Box
      sx={{
        background: `linear-gradient(145deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        borderRadius: "8px",
        p: 2,
        height: ADMIN_TOP_CARD_HEIGHT,
        minHeight: ADMIN_TOP_CARD_HEIGHT,
        maxHeight: ADMIN_TOP_CARD_HEIGHT,
        boxSizing: "border-box",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
        border: `2px solid ${borderColor}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-between",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {Icon && (
        <Icon
          sx={{
            position: "absolute",
            left: -8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 256,
            color: iconGhostColor,
            opacity: 0.15,
            pointerEvents: "none",
          }}
        />
      )}

      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ color: titleColor, fontWeight: 700, lineHeight: 1.25, fontSize: "0.9375rem" }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="body2"
            sx={{
              color: titleColor,
              mt: 0.75,
              lineHeight: 1.35,
              wordBreak: "break-word",
              fontWeight: 600,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ minWidth: 0, width: "100%" }}>
        <Typography
          component="div"
          {...(!textValue && { noWrap: true })}
          sx={textValue ? {
            color: valueColor,
            fontWeight: 800,
            lineHeight: 1.2,
            fontSize: "1.05rem",
            textAlign: "right",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          } : {
            color: valueColor,
            fontWeight: 800,
            lineHeight: 1.05,
            fontSize: { xs: "2.25rem", sm: "2.75rem" },
            letterSpacing: "-0.02em",
            textAlign: "right",
          }}
        >
          {display}
        </Typography>
        {caption ? (
          <Typography
            variant="caption"
            sx={{ color: titleColor, opacity: 0.85, display: "block", textAlign: "right" }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default AdminStatCard;
